/**
 * Statische App-Konfiguration. Wird in `_getAppConfiguration()` der App-Klasse
 * zurückgegeben. Hier liegen Firmendaten, Default-Locale, Footer-Settings.
 *
 * Stadt-Bezug (Köln) ist bewusst NUR Inhalt/Konfiguration — nie Struktur:
 * für ein weiteres Städte-Portal werden nur diese Werte, Branding und
 * Datenquellen getauscht.
 */

/**
 * Der Betreiber — die EINE Stelle für Firmendaten (Fuß, Impressum,
 * Datenschutz „Verantwortliche Stelle"). Ein anderer Betreiber ist möglich;
 * dann wird hier getauscht, nicht in den Seiten.
 *
 * Die Impressumsseite zeigt nur Felder, die gefüllt sind — ein leeres Feld
 * erscheint gar nicht, statt als leere Zeile. OFFEN und rechtlich nötig
 * (§ 5 DDG): `street`/`streetNr` (ladungsfähige Anschrift — Ort und PLZ
 * allein genügen nicht), `managingDirector` (Vertretungsberechtigte:r) und
 * `register` (Registergericht + HRB der UG). Solange sie leer sind, ist das
 * Impressum unvollständig.
 */
export const COMPANY_CONTACT = {
  business: true,
  // VORLÄUFIG ohne "(haftungsbeschränkt)": Der Zusatz ist die korrekte
  // Firmierung und nach § 5a GmbHG Pflicht — im IMPRESSUM. Im Fuß war er zu
  // lang. Sauber wäre eine zweite Konstante (Kurzname für die Anzeige,
  // volle Firmierung fürs Impressum); vor dem Deploy nachziehen.
  companyName: "PD Progressive Development", // vorerst; ein anderer Betreiber ist möglich
  vatNr: "DE343503322",
  street: "",
  streetNr: "",
  zip: "51061",
  city: "Köln",
  phone1: "",
  email: "info@progressive-development.com",
  /** Vertretungsberechtigte:r nach § 5 Abs. 1 Nr. 1 DDG. */
  managingDirector: "",
  /** Registergericht und Nummer, z. B. "Amtsgericht Köln, HRB 12345". */
  register: "",
};

/**
 * Der Domainname — zugleich der Alternativtext der Wortmarke im Kopf.
 * Die sichtbare Wortmarke (public/marke/logo-*.svg) trägt denselben Namen,
 * zweizeilig gesetzt. Sie entsteht aus `WORTMARKE` in
 * docs/changedoc/entwurf-marke/bau-marke.py — dort liegt auch die frühere
 * Fassung, falls politik.koeln doch noch gekauft wird.
 */
export const APP_NAME = "stadtpolitik.koeln";

/**
 * Die Stadt, über die dieses Portal berichtet — Inhalt, nicht Struktur.
 * Steht in Überschriften ("So hat Köln gewählt"); für ein weiteres
 * Städte-Portal wird nur dieser Wert getauscht.
 */
export const STADT_NAME = "Köln";

/**
 * Der Portalname im Seitenkopf — ausgeschrieben, nicht als Domain.
 *
 * `APP_NAME` ist der Domainname und steht als Logo im Kopf der Anwendung;
 * dies hier ist die Einordnung IN der Seite („Politik Köln"), die Zeile mit
 * der Haarlinie darunter. Für ein weiteres Städte-Portal wird nur dieser Wert
 * getauscht.
 */
export const PORTAL_TITEL = `Politik ${STADT_NAME}`;

/**
 * Die Adresse für Rückmeldungen — VORLÄUFIG die des Betreibers.
 *
 * `support@stadtpolitik.koeln` gibt es noch nicht; bis das Postfach steht
 * (AUFTRAG-livegang.md, S3) liefe jede Rückmeldung ins Leere. Nebeneffekt:
 * Der Fuß-Block nennt PD und zeigt jetzt auch eine PD-Adresse — vorher stand
 * dort eine Portal-Adresse unter dem Namen des Hauses.
 *
 * Wirkt an vier Stellen: Fuß, „Fehler melden" auf /quellen, Kontaktaufnahme
 * in der Datenschutzerklärung und der Testbetriebs-Hinweis, sobald er kommt.
 */
export const SUPPORT_EMAIL = "info@progressive-development.com";

/**
 * Pflicht-Attribution der Datenquelle (Lizenzauflage, Abschnitt 6 der
 * Anforderungen) — Wortlaut ist EXAKT vorgegeben, nicht umformulieren.
 * v1 statisch im Footer; perspektivisch je Objekt aus den Provenance-Feldern
 * (`attributionText`) generiert.
 */
export const DATA_SOURCE_ATTRIBUTION = "Datenquelle: Stadt Köln – offenedaten-koeln.de";

/**
 * Woher die App ihre Artefakte holt — Dateien, die die Pipeline je Lauf
 * ganz schreibt (Drucksachen-Liste, künftig Suchindex und Geometrien).
 *
 * Die öffentliche URL des Storage-Buckets (`VITE_ARTEFAKT_BASIS`, ohne
 * Schrägstrich am Ende) — derselbe Bucket wie die Geometrien; die Pipeline
 * legt die Dateien dort ab (`RUECKMELDUNG-drucksachen-liste-artefakt.md`).
 * Ohne Eintrag zeigt die Liste ehrlich, dass sie nicht bereitsteht.
 */
export const ARTEFAKT_BASIS: string =
  (import.meta.env.VITE_ARTEFAKT_BASIS as string | undefined) || "";

/** Externe Links — Platzhalter, bis Repo/Deployment stehen. */
export const EXTERNAL_LINKS = {
  github: "https://github.com/progressive-development", // TBD: Repo-URL
  /** Menschenlesbares RIS-Pendant zum Gegenprüfen der Daten. */
  ris: "https://ratsinformation.stadt-koeln.de/",
  /** Datensatzseite mit Lizenz. */
  openData: "https://offenedaten-koeln.de/dataset/oparl-api-koeln",
};
